import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import { Alert, BrandLockup, Button, Card, Field, Input, Page, Stack } from '@integra/ui';
import { useOrganisation, useUpdateOrganisationBranding } from '../api/hooks.js';
import { useAuthStore } from '../auth/store.js';
import type { OrganisationBranding } from '../api/types.js';

/**
 * Organisation branding (§20.5 agency multi-brand). White-labels the app chrome
 * for a whole organisation: product name, logo, and brand colours. An agency
 * reselling INTEGRA presents each client's own identity here, never the Fruitful
 * Bough estate brand — and never a hardcoded client name (§4.1).
 *
 * Colours are entered and previewed as DATA (a hex string painted onto an SVG
 * swatch and the lockup mark), the §21.4 pattern the Creative studio already
 * uses. The saved colours are injected onto the document root by the shell.
 */

const HEX = /^#[0-9a-fA-F]{3,8}$/;
// A logo data-URI must fit the server's ~512KB base64 cap comfortably; guard the
// raw file at 384KB so the base64 expansion (~4/3) stays within budget.
const MAX_LOGO_BYTES = 384 * 1024;

interface BrandForm {
  productName: string;
  tagline: string;
  logoUrl: string;
  primaryColour: string;
  accentColour: string;
}

const EMPTY_FORM: BrandForm = {
  productName: '',
  tagline: '',
  logoUrl: '',
  primaryColour: '',
  accentColour: '',
};

function toForm(branding: OrganisationBranding): BrandForm {
  return {
    productName: branding.productName ?? '',
    tagline: branding.tagline ?? '',
    logoUrl: branding.logoUrl ?? '',
    primaryColour: branding.primaryColour ?? '',
    accentColour: branding.accentColour ?? '',
  };
}

/** A live colour chip — the hex painted as SVG fill (data), never inline style. */
function Swatch({ hex }: { hex: string }) {
  const valid = HEX.test(hex);
  return (
    <svg
      width="34"
      height="34"
      viewBox="0 0 34 34"
      role="img"
      aria-label={valid ? `${hex} preview` : 'no colour set'}
      className="shrink-0 rounded-md border border-edge-strong"
    >
      <rect width="34" height="34" fill={valid ? hex : 'transparent'} />
    </svg>
  );
}

export function BrandingPage() {
  const organisationId = useAuthStore((s) => s.session?.organisationId ?? '');
  const { data: organisation, isLoading } = useOrganisation(organisationId);
  const save = useUpdateOrganisationBranding(organisationId);

  const [form, setForm] = useState<BrandForm>(EMPTY_FORM);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Seed the form once the org's stored branding arrives.
  useEffect(() => {
    if (organisation !== undefined) setForm(toForm(organisation.branding));
  }, [organisation]);

  const set =
    (key: keyof BrandForm) =>
    (value: string): void => {
      setForm((f) => ({ ...f, [key]: value }));
      setSaved(false);
    };

  const primaryInvalid = form.primaryColour !== '' && !HEX.test(form.primaryColour);
  const accentInvalid = form.accentColour !== '' && !HEX.test(form.accentColour);
  const canSave = !primaryInvalid && !accentInvalid && logoError === null;

  // Preview mirrors how the shell resolves the brand: a set product name
  // white-labels (dropping the estate tagline), otherwise the estate identity.
  const preview = useMemo(() => {
    const whiteLabelled = form.productName.trim() !== '';
    const productName = whiteLabelled ? form.productName.trim() : 'INTEGRA';
    const tagline = whiteLabelled
      ? form.tagline.trim() === ''
        ? null
        : form.tagline.trim()
      : form.tagline.trim() === ''
        ? 'Fruitful Bough'
        : form.tagline.trim();
    const logoUrl = form.logoUrl.trim() === '' ? null : form.logoUrl.trim();
    const markColour = HEX.test(form.primaryColour) ? form.primaryColour : undefined;
    return { productName, tagline, logoUrl, markColour };
  }, [form]);

  const onLogoFile = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file === undefined) return;
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError('Logo is too large — use an image under 384KB, or paste a hosted URL.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setLogoError(null);
      set('logoUrl')(typeof reader.result === 'string' ? reader.result : '');
    };
    reader.onerror = () => setLogoError('Could not read that file.');
    reader.readAsDataURL(file);
  };

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (!canSave) return;
    const payload: OrganisationBranding = {
      productName: form.productName.trim() === '' ? null : form.productName.trim(),
      tagline: form.tagline.trim() === '' ? null : form.tagline.trim(),
      logoUrl: form.logoUrl.trim() === '' ? null : form.logoUrl.trim(),
      primaryColour: form.primaryColour === '' ? null : form.primaryColour,
      accentColour: form.accentColour === '' ? null : form.accentColour,
    };
    save.mutate(payload, { onSuccess: () => setSaved(true) });
  };

  const resetToEstate = (): void => {
    setForm(EMPTY_FORM);
    setLogoError(null);
    save.mutate(
      {
        productName: null,
        tagline: null,
        logoUrl: null,
        primaryColour: null,
        accentColour: null,
      },
      { onSuccess: () => setSaved(true) },
    );
  };

  return (
    <Page
      title="Customise & branding"
      description="Make the platform your own. Set the product name, logo, and brand colours for your whole organisation — everyone here sees your identity across the app, consistently, not per workspace."
    >
      <Stack gap="lg">
        {isLoading ? (
          <p className="text-sm text-ink-muted">Loading…</p>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
            <Card>
              <form onSubmit={submit}>
                <Stack gap="lg">
                  <Field
                    label="Product name"
                    htmlFor="brand-product-name"
                    hint="Shown in place of “INTEGRA”. Leave blank to keep the default."
                  >
                    <Input
                      id="brand-product-name"
                      value={form.productName}
                      maxLength={80}
                      placeholder="INTEGRA"
                      onChange={(e) => set('productName')(e.target.value)}
                    />
                  </Field>

                  <Field
                    label="Tagline"
                    htmlFor="brand-tagline"
                    hint="A short line under the name. Blank hides it for a white-labelled brand."
                  >
                    <Input
                      id="brand-tagline"
                      value={form.tagline}
                      maxLength={80}
                      placeholder="Fruitful Bough"
                      onChange={(e) => set('tagline')(e.target.value)}
                    />
                  </Field>

                  <Field
                    label="Logo image"
                    htmlFor="brand-logo-url"
                    hint="An https link to a logo, or upload a file (under 384KB). Blank shows the INTEGRA mark."
                    error={logoError ?? undefined}
                  >
                    <Stack gap="sm">
                      <Input
                        id="brand-logo-url"
                        value={form.logoUrl}
                        placeholder="https://…/logo.svg"
                        onChange={(e) => {
                          setLogoError(null);
                          set('logoUrl')(e.target.value);
                        }}
                      />
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                        onChange={onLogoFile}
                        className="text-xs text-ink-muted file:mr-3 file:rounded-md file:border file:border-edge file:bg-surface-sunken file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-navy-700"
                      />
                      {form.logoUrl !== '' && (
                        <Button variant="ghost" type="button" onClick={() => set('logoUrl')('')}>
                          Remove logo
                        </Button>
                      )}
                    </Stack>
                  </Field>

                  <Field
                    label="Primary colour"
                    htmlFor="brand-primary"
                    hint="Hex, e.g. #b21e3a. Colours the brand mark and accents."
                    error={primaryInvalid ? 'Enter a hex colour like #b21e3a.' : undefined}
                  >
                    <div className="flex items-center gap-3">
                      <Swatch hex={form.primaryColour} />
                      <Input
                        id="brand-primary"
                        value={form.primaryColour}
                        placeholder="#b21e3a"
                        invalid={primaryInvalid}
                        onChange={(e) => set('primaryColour')(e.target.value.trim())}
                      />
                    </div>
                  </Field>

                  <Field
                    label="Accent colour"
                    htmlFor="brand-accent"
                    hint="Hex. A secondary brand colour for future accents and exports."
                    error={accentInvalid ? 'Enter a hex colour like #0e2340.' : undefined}
                  >
                    <div className="flex items-center gap-3">
                      <Swatch hex={form.accentColour} />
                      <Input
                        id="brand-accent"
                        value={form.accentColour}
                        placeholder="#0e2340"
                        invalid={accentInvalid}
                        onChange={(e) => set('accentColour')(e.target.value.trim())}
                      />
                    </div>
                  </Field>

                  {save.isError && (
                    <Alert tone="error" title="Could not save branding">
                      Something went wrong saving your branding. Please try again.
                    </Alert>
                  )}
                  {saved && !save.isPending && (
                    <Alert tone="success" title="Branding saved">
                      Your organisation’s branding is live across the app.
                    </Alert>
                  )}

                  <div className="flex flex-wrap items-center gap-3">
                    <Button type="submit" disabled={!canSave || save.isPending}>
                      {save.isPending ? 'Saving…' : 'Save branding'}
                    </Button>
                    <Button variant="ghost" type="button" onClick={resetToEstate}>
                      Reset to default
                    </Button>
                  </div>
                </Stack>
              </form>
            </Card>

            <Card>
              <Stack gap="md">
                <p className="text-xs font-semibold uppercase tracking-widest text-ink-muted">
                  Live preview
                </p>
                {/* The lockup on the navy chrome, exactly as the sidebar renders it. */}
                <div className="rounded-lg bg-navy-700 p-5">
                  <BrandLockup
                    tone="light"
                    productName={preview.productName}
                    tagline={preview.tagline}
                    logoUrl={preview.logoUrl}
                    {...(preview.markColour !== undefined
                      ? { markColour: preview.markColour }
                      : {})}
                  />
                </div>
                <p className="text-xs text-ink-muted">
                  This is how your brand appears in the navigation. Colours also flow to accents
                  across the app once saved.
                </p>
              </Stack>
            </Card>
          </div>
        )}
      </Stack>
    </Page>
  );
}
